/**
 * Context Explorer (V3 screen 01) — a DRILL surface opened from a context, not
 * the default home. It renders the authorized context graph for a root context
 * through the server-side Explorer renderer (a ViewPacketV3), under a chosen
 * Lens and semantic level.
 *
 * Doctrine (mirrors DecisionRoom + the §"State Requirements" eight-state
 * contract): this is a READ projection of authorized data. It never mutates
 * business truth; every drill action navigates to a server-enforced surface.
 * The packet's own `overflow` / `redactions` / `meta.truncated` markers drive
 * the shared {@link RenderState} so a truncated or partly-redacted graph is
 * always disclosed rather than silently dropped.
 *
 * Backend consumed (read-only):
 *   GET /api/lenses                              — the Lens registry
 *   GET /api/contexts/:id/explorer?lens_id=…     — the ViewPacketV3 render
 * If no published Lens exists the screen degrades to a truthful "proposed"
 * empty state (the render family needs a Lens id).
 */
import * as React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useGo } from "@refinedev/core";

import type { ViewPacketV3, ViewSemanticLevel } from "@aos/shared";
import { VIEW_SEMANTIC_LEVELS } from "@aos/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Icon } from "@/components/ui/icon";
import { ErrorState } from "@/components/ui/states";
import { RenderState, type RenderStateKind } from "@/components/app/RenderState";
import { StatusBadge } from "@/components/app/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import { Field, FieldGrid, InspectorSection, RefLink } from "@/components/inspector";
import {
  EntityGraph,
  type EntityGraphEdge,
  type EntityGraphNode,
} from "@/components/graph/entity-graph";
import { api } from "@/lib/api";
import { formatDateTime, humanize } from "@/lib/format";

/** A Lens row (subset of the registry record the switcher needs). */
interface ExplorerLens {
  id: string;
  name: string;
  version: string;
  maturity: string;
}

interface LensListResponse {
  items: ExplorerLens[];
  total: number;
}

/** Map a ViewPacketV3 node kind to the EntityGraph card `type` (drives glyph). */
function nodeType(kind: string): string {
  switch (kind) {
    case "project":
      return "project";
    case "package":
      return "package";
    case "artifact":
      return "artifact";
    case "decision":
      return "decision";
    case "run":
      return "process";
    case "cluster":
      return "cluster";
    case "redacted":
      return "redacted";
    default:
      return "context";
  }
}

/** Project a ViewPacketV3 into the EntityGraph node/edge contract. */
function toGraph(packet: ViewPacketV3): {
  nodes: EntityGraphNode[];
  edges: EntityGraphEdge[];
} {
  const nodes: EntityGraphNode[] = packet.nodes.map((node) => ({
    id: node.id,
    title: node.label || node.id,
    type: nodeType(node.kind),
    subtitle: node.status?.label ? humanize(node.status.label) : humanize(node.kind),
    tag: humanize(node.kind),
    cluster: node.cluster_id ?? node.kind,
    meta: { ...node.meta, kind: node.kind, status: node.status, context_id: node.context_id },
  }));
  const edges: EntityGraphEdge[] = packet.edges.map((edge) => ({
    id: edge.id,
    from: edge.source,
    to: edge.target,
    label: edge.label ? humanize(edge.label) : humanize(edge.kind),
  }));
  return { nodes, edges };
}

interface SelectedNode {
  id: string;
  title: string;
  kind: string;
  statusLabel: string | null;
  contextId: string | null;
}

function useExplorerLenses() {
  const [data, setData] = React.useState<ExplorerLens[] | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<LensListResponse>("/lenses", { query: { pageSize: 200 } })
      .then((res) => {
        if (!cancelled) setData(res.items);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load the Lens registry.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { data, loading, error };
}

function useExplorerPacket(
  contextId: string,
  lensId: string | null,
  semanticLevel: ViewSemanticLevel,
) {
  const [data, setData] = React.useState<ViewPacketV3 | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const reload = React.useCallback(() => setReloadKey((value) => value + 1), []);

  React.useEffect(() => {
    if (!lensId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<ViewPacketV3>(`/contexts/${encodeURIComponent(contextId)}/explorer`, {
        query: { lens_id: lensId, semantic_level: semanticLevel },
      })
      .then((packet) => {
        if (!cancelled) setData(packet);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to render the Context Explorer.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [contextId, lensId, semanticLevel, reloadKey]);

  return { data, loading, error, reload };
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <Card>
      <CardHeader className="p-3 pb-1">
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        <p className="font-mono text-sm tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

function SelectionInspector({
  selected,
  packet,
}: {
  selected: SelectedNode | null;
  packet: ViewPacketV3;
}) {
  if (!selected) {
    return (
      <p className="text-sm text-muted-foreground">
        Select a node in the graph to inspect it. The canvas shows every authorized
        node for this root under the chosen Lens.
      </p>
    );
  }
  return (
    <FieldGrid>
      <Field label="Node">
        <span className="[overflow-wrap:anywhere]">{selected.title}</span>
      </Field>
      <Field label="Kind">
        <Badge variant="outline">{humanize(selected.kind)}</Badge>
      </Field>
      <Field label="Status">
        {selected.statusLabel ? <StatusBadge status={selected.statusLabel} /> : "—"}
      </Field>
      <Field label="Context">
        {selected.contextId ? (
          <RefLink resource="contexts" id={selected.contextId} />
        ) : (
          <span className="font-mono text-xs">{selected.id}</span>
        )}
      </Field>
      <Field label="Lens">
        <span className="font-mono text-xs">
          {packet.meta.lens_id} · {packet.meta.lens_version}
        </span>
      </Field>
      <Field label="Level">{packet.meta.semantic_level}</Field>
    </FieldGrid>
  );
}

function ExplorerContent({
  packet,
  reload,
}: {
  packet: ViewPacketV3;
  reload: () => void;
}) {
  const go = useGo();
  const [selected, setSelected] = React.useState<SelectedNode | null>(null);
  const graph = React.useMemo(() => toGraph(packet), [packet]);

  const handleNodeClick = React.useCallback((node: EntityGraphNode) => {
    const meta = node.meta ?? {};
    const status = meta.status as { label?: string } | undefined;
    setSelected({
      id: node.id,
      title: node.title,
      kind: typeof meta.kind === "string" ? meta.kind : node.type,
      statusLabel: status?.label ?? null,
      contextId: typeof meta.context_id === "string" ? meta.context_id : null,
    });
  }, []);

  // Pick the body state from the packet's own disclosure markers. A graph with
  // hidden nodes/edges renders `overflow`; a redacted packet renders `redacted`;
  // otherwise `normal`. (Stale is reserved for a freshness budget the renderer
  // does not yet emit.)
  const hidden =
    packet.overflow.hidden_node_count + packet.overflow.hidden_edge_count;
  const bodyState: RenderStateKind =
    packet.nodes.length === 0
      ? "empty"
      : packet.redactions.length > 0
        ? "redacted"
        : hidden > 0
          ? "overflow"
          : "normal";

  const graphBlock = (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(280px,0.9fr)]">
      <Card className="overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between gap-3 p-3">
          <CardTitle className="text-sm">Authorized graph</CardTitle>
          <span className="font-mono text-xs text-muted-foreground">
            {packet.meta.node_count} nodes · {packet.meta.edge_count} edges
          </span>
        </CardHeader>
        <CardContent className="p-0">
          <EntityGraph
            nodes={graph.nodes}
            edges={graph.edges}
            layoutMode="clusterGrid"
            onNodeClick={handleNodeClick}
            height="62vh"
            emptyMessage="No authorized nodes in this view."
            className="rounded-none border-0"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-sm">Inspector</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <SelectionInspector selected={selected} packet={packet} />
          {selected ? (
            <Button
              className="mt-4"
              variant="outline"
              size="sm"
              disabled={!selected.contextId}
              onClick={() =>
                selected.contextId
                  ? go({ to: `/contexts/${selected.contextId}` })
                  : undefined
              }
            >
              <Icon name="deepLink" data-icon="inline-start" aria-hidden />
              Open context
            </Button>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        <Metric label="Nodes" value={packet.meta.node_count} />
        <Metric label="Edges" value={packet.meta.edge_count} />
        <Metric label="Level" value={packet.meta.semantic_level} />
        <Metric label="Max nodes" value={packet.meta.budget.max_nodes} />
        <Metric label="Hidden nodes" value={packet.overflow.hidden_node_count} />
        <Metric label="Hidden edges" value={packet.overflow.hidden_edge_count} />
      </div>

      <InspectorSection
        title="View"
        aside={
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              Generated {formatDateTime(packet.meta.generated_at)}
            </span>
            <Button variant="outline" size="sm" onClick={reload}>
              <Icon name="reopen" data-icon="inline-start" aria-hidden />
              Refresh
            </Button>
          </div>
        }
      >
        <FieldGrid>
          <Field label="Root">
            {packet.meta.root_context_ids[0] ? (
              <RefLink resource="contexts" id={packet.meta.root_context_ids[0]} />
            ) : (
              "—"
            )}
          </Field>
          <Field label="Lens">
            <span className="font-mono text-xs">
              {packet.meta.lens_id} · {packet.meta.lens_version}
            </span>
          </Field>
          <Field label="Truncated">{packet.meta.truncated ? "Yes" : "No"}</Field>
          <Field label="Access fingerprint">
            <span className="font-mono text-xs [overflow-wrap:anywhere]">
              {packet.meta.access_fingerprint}
            </span>
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Graph">
        <RenderState
          state={bodyState}
          empty={{
            icon: "scope",
            title: "No authorized records match this view.",
            hint: "Bind contexts or entities to this root, or widen the Lens, to populate the graph.",
          }}
          overflowCount={hidden}
          overflowMessage={
            hidden > 0
              ? `${packet.overflow.hidden_node_count} node(s) and ${packet.overflow.hidden_edge_count} edge(s) are hidden by the Lens budget.`
              : undefined
          }
          redactedCount={packet.redactions.length}
          redactedMessage="Some nodes or edges are withheld by access policy; counts are shown as aggregates."
        >
          {graphBlock}
        </RenderState>
      </InspectorSection>
    </div>
  );
}

/** Pick the default Lens: prefer a published one, else the first available. */
function defaultLens(lenses: ExplorerLens[]): ExplorerLens | null {
  return lenses.find((lens) => lens.maturity === "published") ?? lenses[0] ?? null;
}

export function ContextExplorerRoute() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <ErrorState message="Missing context id." />;
  return <ContextExplorerPage contextId={id} />;
}

function ContextExplorerPage({ contextId }: { contextId: string }) {
  const go = useGo();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: lenses, loading: lensesLoading, error: lensesError } = useExplorerLenses();

  const semanticLevel = ((): ViewSemanticLevel => {
    const raw = searchParams.get("level");
    return VIEW_SEMANTIC_LEVELS.includes(raw as ViewSemanticLevel)
      ? (raw as ViewSemanticLevel)
      : "L1";
  })();

  const urlLensId = searchParams.get("lens_id");
  const activeLensId =
    urlLensId ?? (lenses ? (defaultLens(lenses)?.id ?? null) : null);

  const { data, loading, error, reload } = useExplorerPacket(
    contextId,
    activeLensId,
    semanticLevel,
  );

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const header = (
    <PageHeader
      title="Context Explorer"
      subtitle="Drill the authorized context graph for this root: nodes, typed edges, budget, and redaction disclosure under a chosen Lens."
      icon="contexts"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {lenses && lenses.length > 0 ? (
            <Select
              value={activeLensId ?? undefined}
              onValueChange={(value) => setParam("lens_id", value)}
            >
              <SelectTrigger className="h-9 w-[200px]" aria-label="Lens">
                <SelectValue placeholder="Lens" />
              </SelectTrigger>
              <SelectContent>
                {lenses.map((lens) => (
                  <SelectItem key={lens.id} value={lens.id}>
                    {lens.name} · {lens.version}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          <Select value={semanticLevel} onValueChange={(value) => setParam("level", value)}>
            <SelectTrigger className="h-9 w-[110px]" aria-label="Semantic level">
              <SelectValue placeholder="Level" />
            </SelectTrigger>
            <SelectContent>
              {VIEW_SEMANTIC_LEVELS.map((level) => (
                <SelectItem key={level} value={level}>
                  {level}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => go({ to: `/contexts/${contextId}` })}>
            <Icon name="contexts" data-icon="inline-start" aria-hidden />
            Context
          </Button>
        </div>
      }
    />
  );

  // The screen body routes through the eight-state contract. Loading/error are
  // owned here (the fetch lifecycle); the populated content renders its own
  // empty/overflow/redacted/normal from the packet's disclosure markers.
  let body: React.ReactNode;
  if (lensesLoading || (activeLensId && loading)) {
    body = <RenderState state="loading" loadingRows={6} />;
  } else if (lensesError) {
    body = <RenderState state="error" errorMessage={lensesError} />;
  } else if (!lenses || lenses.length === 0) {
    // No Lens registry → the render family has nothing to project. Truthful
    // "proposed" empty state rather than a fabricated graph.
    body = (
      <RenderState
        state="empty"
        empty={{
          icon: "graph",
          title: "No Lens is published yet (proposed).",
          hint: "The Context Explorer renders through a Lens. Publish a Lens in the registry to drill this context's authorized graph.",
        }}
      />
    );
  } else if (error) {
    body = <RenderState state="error" errorMessage={error} onRetry={reload} />;
  } else if (data) {
    body = <ExplorerContent packet={data} reload={reload} />;
  } else {
    body = <RenderState state="loading" loadingRows={6} />;
  }

  return (
    <div className="max-w-6xl">
      <div className="flex flex-col gap-6">
        {header}
        {body}
      </div>
    </div>
  );
}

export default ContextExplorerRoute;
